import React, { useState } from 'react';
import { ArrowUpRight, Mail, X } from 'lucide-react';
import { billingKontoAPI as billingAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * The plan limit is an upgrade option, never an error (RULES.md 7.5).
 *
 * The owner typed an email, hit the seat limit and gets this modal. Confirming
 * upgrades through the same path the customer uses and sends the invite he
 * already typed, in the same flow — he never types the email again. On the
 * biggest pakki there is no upgrade to offer, so he gets a real mailto link.
 */
export const PlanUpgradeModal = ({ limit, invite, onClose, onUpgraded }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  if (!limit) return null;

  const who = (invite?.name || invite?.email || '').trim();
  // Node/browser locale data for is-IS is not guaranteed in every build, so
  // the thousands separator is written out rather than guessed.
  const isk = (n) => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const atTheTop = !limit.next_plan;
  // A stjórnandi sees the pakki and its price but not the buy button: paying
  // for a bigger pakki is the owner's decision.
  const ownerOnly = !!user?.role && user.role !== 'owner';

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      // With a typed invite the upgrade sends it in the same request. Without
      // one — an invite already pending that lost its seat — only the pakki
      // moves; the invitation is already on file and needs no retyping.
      const res = invite?.email
        ? await billingAPI.upgradeAndAddUser({
            plan: limit.next_plan,
            invite: { email: invite.email, role: invite.role || 'worker',
                      name: invite.name || '' },
          })
        : await billingAPI.changePlan(limit.next_plan);
      onUpgraded?.(res.data);
    } catch (e) {
      setError(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || 'Ekki tókst að uppfæra. Reyndu aftur.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="plan-upgrade-modal"
    >
      <div className="w-full sm:max-w-md bg-card border-t sm:border border-border sm:rounded-2xl rounded-t-2xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold" data-testid="plan-upgrade-title">
            {atTheTop ? 'Stærsti pakkinn' : 'Fleiri notendur'}
          </h2>
          <button
            onClick={onClose}
            className="text-foreground-muted hover:text-foreground"
            data-testid="plan-upgrade-close"
            aria-label="Loka"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-foreground-secondary leading-relaxed" data-testid="plan-upgrade-body">
          {atTheTop ? (
            <>
              {limit.plan_name} leyfir allt að {limit.max_users} notendur, sem er
              stærsti pakkinn. Hafðu samband og við finnum lausn fyrir þig.
            </>
          ) : (
            <>
              {limit.plan_name} leyfir allt að {limit.max_users}{' '}
              {limit.max_users === 1 ? 'notanda' : 'notendur'}. Til að bæta{' '}
              {who || 'notandanum'} við skiptirðu yfir í {limit.next_plan_name},
              allt að {limit.next_max_users} notendur,{' '}
              {isk(limit.next_price_isk)} kr á mánuði.
            </>
          )}
        </p>

        {limit.pending_invites > 0 && !atTheTop && (
          <p className="text-xs text-foreground-muted" data-testid="plan-upgrade-seats">
            Talið með: {limit.member_users}{' '}
            {limit.member_users === 1 ? 'notandi' : 'notendur'} og{' '}
            {limit.pending_invites} ósamþykkt boð.
          </p>
        )}

        {error && (
          <p className="text-sm text-danger" data-testid="plan-upgrade-error">{error}</p>
        )}

        {atTheTop ? (
          <a
            href={`mailto:${limit.support_email}?subject=${encodeURIComponent('Fleiri notendur í Rektra')}`}
            className="btn btn-primary w-full"
            data-testid="plan-upgrade-contact"
          >
            <Mail size={16} />
            Hafðu samband
          </a>
        ) : ownerOnly ? (
          <p className="text-sm font-medium text-amber-400" data-testid="plan-upgrade-owner-only">
            Aðeins eigandi getur breytt pakka.
          </p>
        ) : (
          <button
            onClick={confirm}
            disabled={busy}
            className="btn btn-primary w-full"
            data-testid="plan-upgrade-confirm"
          >
            {busy ? <div className="spinner w-5 h-5" /> : (
              <><ArrowUpRight size={16} />
              {who ? `Skipta og bæta ${who} við` : `Skipta yfir í ${limit.next_plan_name}`}</>
            )}
          </button>
        )}

        <button
          onClick={onClose}
          disabled={busy}
          className="btn btn-secondary w-full"
          data-testid="plan-upgrade-cancel"
        >
          Hætta við
        </button>

        {!atTheTop && !ownerOnly && (
          <p className="text-xs text-foreground-muted leading-relaxed" data-testid="plan-upgrade-note">
            Nýja hámarkið gildir strax. Verðið breytist á næsta reikningi, ekkert
            er rukkað aukalega í dag.
          </p>
        )}
      </div>
    </div>
  );
};

/** The structured 403 the server raises when the seat limit is reached. */
export const planLimitFrom = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === 'object' && d.code === 'plan_user_limit') return d;
  return null;
};

export default PlanUpgradeModal;
